import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface IntegratedFlow {
  Id: string;
  ExternalId: string;
  IntegrationId: string;
  IntegrationIds?: string[];
  FlowBlueprintId: string;
  FlowTrigger: FlowTrigger;
  IsDefault: boolean;
  IsOvernightJob: boolean;
  RetryAsOvernightJob: boolean;
  CategoryId?: string | null;
  ConcurrencyConfiguration?: ConcurrencyConfiguration;
  CustomData?: any;
  Deleted?: boolean;
}

export interface ConcurrencyConfiguration {
  Limit: number;
  SpecifyByExternalId: boolean;
}

export enum FlowTrigger {
  NoTrigger = 'NoTrigger',
  API = 'API',
  TrengoChat = 'TrengoChat',
  ACITWebhook = 'ACITWebhook'
}

@Injectable({
  providedIn: 'root'
})
export class IntegratedFlowService {
  private api: AxiosInstance | null = null;
  private platformId = inject(PLATFORM_ID);
  private authService = inject(KeycloakAuthService);
  private apiConfig = inject(ApiConfigService);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.api = axios.create({
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Add request interceptor to dynamically set baseURL and include OAuth token
      this.api.interceptors.request.use((config) => {
        // Dynamically construct baseURL on each request to get current tenantId
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/${tenantId}`;

        const token = this.authService.accessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      });
    }
  }

  async getIntegratedFlows(): Promise<Array<IntegratedFlow> | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get('/integratedFlow');
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getIntegratedFlow(id: string): Promise<IntegratedFlow | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get(`/integratedFlow/${id}`);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async updateIntegratedFlow(integratedFlow: IntegratedFlow): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.put(`/integratedFlow/${integratedFlow.Id}`, integratedFlow);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async createIntegratedFlow(integratedFlow: IntegratedFlow): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.post('/integratedFlow', integratedFlow);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async deleteIntegratedFlow(id: string): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.delete(`/integratedFlow/${id}`);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async restoreIntegratedFlow(id: string): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      // Assumes restore is exposed through PATCH until a dedicated restore endpoint is available.
      const response = await this.api.patch(`/integratedFlow/${id}`, { Deleted: false });
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }
}
